import {priority} from '@priority-software/priority-proto-api/lib';
import {showFormClientSideMessage} from '../../../../features/Form/actions/messages';
import {
    ErrorResponse,
    getEnhancedMail
} from '../../../../services/rest/llm/enhanceMail';
import AITexts from '../../../../Localization/AISearch/texts';
import {monitorAction} from '../../../../services/datadog';
import {AIWritingAssistantMonitoring} from '../../../../constants/monitoring';

type DataType = {
    content: string;
    enhancementType: string;
    enhancementOption: string;
    language: string;
};

type CallbacksType = {
    onLoading?: (isLoading: boolean) => void;
    onSuccess?: () => void;
    handleResult: (content: string) => void;
    onErrorClose?: () => void;
};

// Helper function to get monitoring key for enhancement completion
const getEnhancementMonitoringKey = (enhancementType: string, enhancementOption: string): string => {
    if (enhancementType === 'TONE') {
        const toneMonitoringMap = {
            'FORMAL': AIWritingAssistantMonitoring.AI_mailedi_formal,
            'PROFESSIONAL': AIWritingAssistantMonitoring.AI_mailedi_professional,
            'NEUTRAL': AIWritingAssistantMonitoring.AI_mailedi_neutral,
            'POLITE': AIWritingAssistantMonitoring.AI_mailedi_polite,
            'APPRECIATIVE': AIWritingAssistantMonitoring.AI_mailedi_appreciative
        };
        return toneMonitoringMap[enhancementOption] || AIWritingAssistantMonitoring.AI_mailedi_tone;
    } else {
        const actionMonitoringMap = {
            'IMPROVE': AIWritingAssistantMonitoring.AI_mailedi_imprv,
            'SPELLING': AIWritingAssistantMonitoring.AI_mailedi_spell
        };
        return actionMonitoringMap[enhancementType] || AIWritingAssistantMonitoring.AI_mailedi_imprv;
    }
};

export const getAIContentEnhancement = async (data: DataType, callbacks: CallbacksType) => {
    const {onLoading, onErrorClose, handleResult, onSuccess} = callbacks;
    try {
        onLoading?.(true);

        const res = await getEnhancedMail(data);

        if (res.code) {
            throw {code: res.code, message: res.message};
        }

        const htmlResult = res.enhancedContent;
        if (htmlResult) {
            // Monitor successful AI enhancement completion
            try {
                const monitoringKey = getEnhancementMonitoringKey(data.enhancementType, data.enhancementOption);
                monitorAction(monitoringKey, {
                    action: 'enhancement_completed',
                    enhancementType: data.enhancementType,
                    enhancementOption: data.enhancementOption,
                    success: true,
                    contentLength: htmlResult.length,
                    language: data.language,
                    timestamp: Date.now(),
                    component: 'EmailWritingAssistantPlugin'
                });
            } catch (monitoringError) {
                // Monitoring should not affect functionality
                console.warn('AI monitoring failed:', monitoringError);
            }

            handleResult(htmlResult);
            onSuccess?.();
        }
    } catch (error) {
        // Monitor AI enhancement errors
        try {
            monitorAction(AIWritingAssistantMonitoring.AI_mailedi_err, {
                action: 'enhancement_error',
                enhancementType: data.enhancementType,
                enhancementOption: data.enhancementOption,
                errorCode: (error as any).code,
                errorMessage: (error as any).message || 'Unknown error',
                language: data.language,
                timestamp: Date.now(),
                component: 'EmailWritingAssistantPlugin'
            });
        } catch (monitoringError) {
            // Monitoring should not affect functionality
            console.warn('AI monitoring failed:', monitoringError);
        }

        const errorTitle =
            (error as any).code === 2
                ? (error as ErrorResponse).message
                : AITexts.ai_email_enhancement_error;

        showFormClientSideMessage('', priority.netitems.shared.Message.Type.Error, {
            title: errorTitle,
            onClose: () => {
                onErrorClose?.();
            }
        });
    } finally {
        onLoading(false);
    }
};
